// Unit conversion utilities for metric/imperial systems

export const UNIT_SYSTEMS = {
  metric: {
    id: 'metric',
    label: { fr: 'Métrique (kg, cm)', en: 'Metric (kg, cm)' },
    weight: 'kg',
    height: 'cm',
    weightLabel: { fr: 'kg', en: 'kg' },
    heightLabel: { fr: 'cm', en: 'cm' },
  },
  imperial: {
    id: 'imperial',
    label: { fr: 'Impérial (lbs, ft)', en: 'Imperial (lbs, ft)' },
    weight: 'lbs',
    height: 'ft',
    weightLabel: { fr: 'lbs', en: 'lbs' },
    heightLabel: { fr: 'pi/po', en: 'ft/in' },
  }
};

// Weight conversions
export const kgToLbs = (kg) => kg * 2.20462;
export const lbsToKg = (lbs) => lbs / 2.20462;

// Height conversions
export const cmToFeetInches = (cm) => {
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return { feet, inches, totalInches };
};

export const feetInchesToCm = (feet, inches = 0) => {
  const totalInches = (feet * 12) + inches;
  return totalInches * 2.54;
};

// Format weight for display
export const formatWeight = (valueKg, unitSystem, language = 'fr') => {
  if (!valueKg) return '-';
  
  if (unitSystem === 'imperial') {
    const lbs = kgToLbs(valueKg);
    return `${Math.round(lbs * 10) / 10} lbs`;
  }
  return `${Math.round(valueKg * 10) / 10} kg`;
};

// Format height for display
export const formatHeight = (valueCm, unitSystem, language = 'fr') => {
  if (!valueCm) return '-';
  
  if (unitSystem === 'imperial') {
    const { feet, inches } = cmToFeetInches(valueCm);
    if (language === 'fr') {
      return `${feet}pi ${inches}po`;
    }
    return `${feet}'${inches}"`;
  }
  return `${Math.round(valueCm)} cm`;
};

// Parse height input (handles both formats)
export const parseHeightInput = (value, unitSystem) => {
  if (unitSystem === 'imperial') {
    // Could be "5'10" or "5 10" or feet and inches separately
    if (typeof value === 'object' && value.feet !== undefined) {
      return feetInchesToCm(value.feet, value.inches || 0);
    }
    // Try to parse string like "5'10" or "5ft 10in"
    const match = String(value).match(/(\d+)['\s]?\s*(\d*)/);
    if (match) {
      return feetInchesToCm(parseInt(match[1]), parseInt(match[2]) || 0);
    }
    return feetInchesToCm(parseFloat(value), 0);
  }
  return parseFloat(value);
};

// Parse weight input
export const parseWeightInput = (value, unitSystem) => {
  const numValue = parseFloat(value);
  if (unitSystem === 'imperial') {
    return lbsToKg(numValue);
  }
  return numValue;
};

// Get default unit system based on language/locale
export const getDefaultUnitSystem = (language) => {
  // US, UK, Canada (sometimes) use imperial
  const imperialLocales = ['en-US', 'en-GB'];
  const locale = navigator.language || 'fr';
  
  if (imperialLocales.includes(locale) || (language === 'en' && locale.startsWith('en-'))) {
    return 'imperial';
  }
  return 'metric';
};

export default {
  UNIT_SYSTEMS,
  kgToLbs,
  lbsToKg,
  cmToFeetInches,
  feetInchesToCm,
  formatWeight,
  formatHeight,
  parseHeightInput,
  parseWeightInput,
  getDefaultUnitSystem,
};
